import { Head, router } from "@inertiajs/react"
import {
  CheckCircle2,
  Clock,
  Hourglass,
  type LucideIcon,
  MailX,
  MoreHorizontal,
  RotateCw,
  Users,
  XCircle,
} from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { EmptyState } from "@/components/empty-state"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Spinner } from "@/components/ui/spinner"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useMenuDialogHandoff } from "@/hooks/use-menu-dialog-handoff"
import { useOnline } from "@/hooks/use-online"
import AppLayout from "@/layouts/app-layout"
import { roleLabel } from "@/lib/role-label"
import { invitationPath, invitationsPath, resendInvitationPath } from "@/routes"
import type {
  InvitationStatus,
  InvitationsIndexProps,
  PendingInvitation,
} from "@/types/invitations"

/**
 * OKS-11 — Invitations/Index.
 *
 * Inviter's pending-invitation list. One table surfaces every status
 * (pending / accepted / expired / revoked) — backend handles sort + filter;
 * frontend just renders and wires row actions.
 *
 * Row actions:
 *   - Resend (pending OR expired) → confirm dialog → `router.post` with
 *     `preserveScroll: true`. Inertia re-renders the list on success.
 *   - Revoke (pending) → DESTRUCTIVE confirm dialog (CTPO D5: no Undo, no
 *     deferred POST) → `router.delete`. Same Inertia re-render semantics.
 *
 * No optimistic UI — Inertia's server response is the source of truth for
 * `status` + `expires_at`. Keeps the list in lockstep with backend state
 * (esp. around the `replace_pending!` branch in PRD Rule 10).
 */
export default function InvitationsIndex({
  invitations,
}: InvitationsIndexProps) {
  const { t, i18n } = useTranslation()
  const online = useOnline()

  const hasInvitations = invitations.length > 0

  // Breadcrumb trail: role home is resolved by the shell itself (dashboard
  // link lives in sidebar). Invitations lives at the top level of its
  // section — single crumb into the current page.
  const breadcrumbs = [
    {
      title: t("invitations.pending.heading"),
      href: invitationsPath(),
    },
  ]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={t("invitations.pending.page_title")} />
      <main className="mx-auto flex w-full max-w-4xl flex-col gap-6 px-6 py-10">
        <header className="flex flex-col gap-1">
          <h1 className="text-foreground text-2xl font-semibold tracking-tight">
            {t("invitations.pending.heading")}
          </h1>
          <p className="text-muted-foreground text-sm">
            {t("invitations.pending.description")}
          </p>
        </header>

        {!online && (
          <Alert data-testid="invitations-offline-alert">
            <AlertDescription>
              {t("invitations.pending.offline_banner")}
            </AlertDescription>
          </Alert>
        )}

        {hasInvitations ? (
          <PendingInvitationsTable
            invitations={invitations}
            online={online}
            locale={i18n.language}
          />
        ) : (
          <EmptyState
            icon={Users}
            title={t("invitations.pending.empty.title")}
            description={t("invitations.pending.empty.description")}
            // OKS-122 — page-scoped testid (role-neutral, no `agency-*` alias).
            // The `/invitations` surface is intentionally role-neutral across
            // every consuming role; see `docs/frontend/invitations.md`.
            data-testid="pending-invites-empty"
          />
        )}
      </main>
    </AppLayout>
  )
}

// ---------------------------------------------------------------------------
// Table
// ---------------------------------------------------------------------------

interface PendingInvitationsTableProps {
  invitations: PendingInvitation[]
  online: boolean
  locale: string
}

function PendingInvitationsTable({
  invitations,
  online,
  locale,
}: PendingInvitationsTableProps) {
  const { t } = useTranslation()

  return (
    <div
      className="overflow-x-auto rounded-lg border"
      data-testid="pending-invites-table"
    >
      <Table>
        <caption className="sr-only">
          {t("invitations.pending.table_caption")}
        </caption>
        <TableHeader>
          <TableRow>
            <TableHead>{t("invitations.pending.col_invitee")}</TableHead>
            <TableHead>{t("invitations.pending.col_role")}</TableHead>
            <TableHead>{t("invitations.pending.col_status")}</TableHead>
            <TableHead>{t("invitations.pending.col_sent_at")}</TableHead>
            <TableHead>{t("invitations.pending.col_expires_at")}</TableHead>
            <TableHead className="w-[1%]">
              <span className="sr-only">
                {t("invitations.pending.col_actions")}
              </span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {invitations.map((invite) => (
            <PendingInvitationRow
              key={invite.id}
              invitation={invite}
              online={online}
              locale={locale}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

interface PendingInvitationRowProps {
  invitation: PendingInvitation
  online: boolean
  locale: string
}

function PendingInvitationRow({
  invitation,
  online,
  locale,
}: PendingInvitationRowProps) {
  const { t } = useTranslation()
  // Backend may emit `type: "admin"` for OKS admin invites (no target
  // tenant). The D1 helper keys on `'brand' | 'agency' | null` — coerce
  // "admin" to null so it routes through the OKS scope (role_labels.oks.*).
  const accountType =
    invitation.target_account.type === "admin"
      ? null
      : invitation.target_account.type
  const role = roleLabel(t, {
    accountType,
    role: invitation.role,
  })

  const canResend =
    (invitation.status === "pending" || invitation.status === "expired") &&
    online
  // Per PRD §3.3 + §5: revoked invites cannot be resent AND only pending
  // invites can be revoked. Accepted invites lock both actions.
  const canRevoke = invitation.status === "pending" && online

  return (
    <TableRow data-testid="pending-invite-row" data-invite-id={invitation.id}>
      <TableCell className="font-medium">{invitation.email}</TableCell>
      <TableCell>{role}</TableCell>
      <TableCell>
        <StatusBadge status={invitation.status} />
      </TableCell>
      <TableCell className="text-muted-foreground whitespace-nowrap">
        {formatDate(invitation.sent_at, locale)}
      </TableCell>
      <TableCell className="text-muted-foreground whitespace-nowrap">
        {formatDate(invitation.expires_at, locale)}
      </TableCell>
      <TableCell>
        {canResend || canRevoke ? (
          <RowActions
            invitation={invitation}
            canResend={canResend}
            canRevoke={canRevoke}
          />
        ) : null}
      </TableCell>
    </TableRow>
  )
}

// ---------------------------------------------------------------------------
// Status badge — icon + text + color (never color-only, UX Spec §3)
// ---------------------------------------------------------------------------

const STATUS_META: Record<
  InvitationStatus,
  { icon: LucideIcon; className: string }
> = {
  pending: {
    icon: Hourglass,
    className: "bg-muted text-muted-foreground border-transparent",
  },
  accepted: {
    icon: CheckCircle2,
    className:
      "bg-emerald-100 text-emerald-900 border-transparent dark:bg-emerald-900/40 dark:text-emerald-100",
  },
  expired: {
    icon: Clock,
    className:
      "bg-amber-100 text-amber-900 border-transparent dark:bg-amber-900/40 dark:text-amber-100",
  },
  revoked: {
    icon: XCircle,
    className:
      "bg-red-100 text-red-900 border-transparent dark:bg-red-900/40 dark:text-red-100",
  },
}

function StatusBadge({ status }: { status: InvitationStatus }) {
  const { t } = useTranslation()
  const meta = STATUS_META[status]
  const Icon = meta.icon
  return (
    <Badge
      className={`gap-1.5 ${meta.className}`}
      aria-label={t(`invitations.pending.status.${status}_aria`)}
      data-testid={`pending-invite-status-${status}`}
    >
      <Icon aria-hidden="true" className="size-3.5" />
      {t(`invitations.pending.status.${status}`)}
    </Badge>
  )
}

// ---------------------------------------------------------------------------
// Row actions — overflow menu + Resend / Revoke dialogs
// ---------------------------------------------------------------------------

interface RowActionsProps {
  invitation: PendingInvitation
  canResend: boolean
  canRevoke: boolean
}

function RowActions({ invitation, canResend, canRevoke }: RowActionsProps) {
  const { t } = useTranslation()
  const [resendOpen, setResendOpen] = useState(false)
  const [revokeOpen, setRevokeOpen] = useState(false)
  const { finalFocus, yieldFocusToDialog } = useMenuDialogHandoff()

  return (
    <div className="flex justify-end">
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              variant="ghost"
              size="icon"
              // Mobile a11y floor is 44px (WCAG 2.5.5 target size); shadcn's
              // default `size="icon"` is 36px. Override on mobile, snap back
              // to 36px at `sm:` to preserve desktop density. Don't change
              // shadcn defaults globally — this is a row-density override.
              className="size-11 sm:size-9"
              aria-label={t("invitations.pending.row_actions_aria", {
                email: invitation.email,
              })}
              data-testid="pending-invite-actions"
            >
              <MoreHorizontal aria-hidden="true" />
            </Button>
          }
        />
        <DropdownMenuContent align="end" finalFocus={finalFocus}>
          {canResend && (
            <DropdownMenuItem
              onClick={() => {
                yieldFocusToDialog()
                setResendOpen(true)
              }}
              data-testid="pending-invite-resend"
            >
              <RotateCw aria-hidden="true" />
              {t("invitations.pending.actions.resend")}
            </DropdownMenuItem>
          )}
          {canRevoke && (
            <DropdownMenuItem
              variant="destructive"
              onClick={() => {
                yieldFocusToDialog()
                setRevokeOpen(true)
              }}
              data-testid="pending-invite-revoke"
            >
              <MailX aria-hidden="true" />
              {t("invitations.pending.actions.revoke")}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <ResendDialog
        invitation={invitation}
        open={resendOpen}
        onOpenChange={setResendOpen}
      />
      <RevokeDialog
        invitation={invitation}
        open={revokeOpen}
        onOpenChange={setRevokeOpen}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Resend dialog
// ---------------------------------------------------------------------------

interface ActionDialogProps {
  invitation: PendingInvitation
  open: boolean
  onOpenChange: (open: boolean) => void
}

function ResendDialog({ invitation, open, onOpenChange }: ActionDialogProps) {
  const { t } = useTranslation()
  const [processing, setProcessing] = useState(false)

  function handleResend() {
    setProcessing(true)
    router.post(
      resendInvitationPath(invitation.id),
      {},
      {
        preserveScroll: true,
        onFinish: () => {
          setProcessing(false)
          onOpenChange(false)
        },
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="pending-invite-resend-dialog">
        <DialogTitle>{t("invitations.pending.resend.title")}</DialogTitle>
        <DialogDescription>
          {t("invitations.pending.resend.body", { email: invitation.email })}
        </DialogDescription>
        <DialogFooter>
          <DialogClose
            render={
              <Button variant="secondary" disabled={processing}>
                {t("invitations.pending.resend.cancel")}
              </Button>
            }
          />
          <Button
            onClick={handleResend}
            disabled={processing}
            data-testid="pending-invite-resend-confirm"
          >
            {processing && <Spinner />}
            {t("invitations.pending.resend.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Revoke dialog (destructive — D5: no Undo)
// ---------------------------------------------------------------------------

function RevokeDialog({ invitation, open, onOpenChange }: ActionDialogProps) {
  const { t } = useTranslation()
  const [processing, setProcessing] = useState(false)

  function handleRevoke() {
    setProcessing(true)
    router.delete(invitationPath(invitation.id), {
      preserveScroll: true,
      onFinish: () => {
        setProcessing(false)
        onOpenChange(false)
      },
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="pending-invite-revoke-dialog">
        <DialogTitle>{t("invitations.pending.revoke.title")}</DialogTitle>
        <DialogDescription>
          {t("invitations.pending.revoke.body", { email: invitation.email })}
        </DialogDescription>
        <DialogFooter>
          {/* Cancel is primary-focus per UX Spec §3 — destructive action is
                NEVER the default focus target of a Dialog. */}
          <DialogClose
            render={
              <Button variant="secondary" autoFocus disabled={processing}>
                {t("invitations.pending.revoke.cancel")}
              </Button>
            }
          />
          <Button
            variant="destructive"
            onClick={handleRevoke}
            disabled={processing}
            data-testid="pending-invite-revoke-confirm"
          >
            {processing && <Spinner />}
            {t("invitations.pending.revoke.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * `Intl.DateTimeFormat` is expensive to construct; we memoise per locale
 * for the page render. Consumers call `formatDate(iso, locale)` without
 * worrying about the cache.
 */
const DATE_FORMATTER_CACHE = new Map<string, Intl.DateTimeFormat>()

function getDateFormatter(locale: string): Intl.DateTimeFormat {
  const cached = DATE_FORMATTER_CACHE.get(locale)
  if (cached) return cached
  const formatter = new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
  DATE_FORMATTER_CACHE.set(locale, formatter)
  return formatter
}

function formatDate(iso: string, locale: string): string {
  return getDateFormatter(locale).format(new Date(iso))
}
